import downloadIcon from "@assets/images/download-teal.svg";
import infoIcon from "@assets/images/info.svg";
import RefreshIcon from "@assets/images/refresh-primary.svg";
import uploadIcon from "@assets/images/upload-button.svg";
import { Box, Button, Grid, Typography } from "@material-ui/core";
import Autocomplete from "@saleor/components/Autocomplete";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField, {
  Choice
} from "@saleor/components/SingleSelectField";
import {
  useGetSaleOrgAndDistChannelBySoldToQuery,
  useSearchSuggestionSoldTosCustMatLazyQuery
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { FormikProps } from "formik";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from "react";
import InlineSVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    root: {
      borderRadius: "0 0 8px 8px",
      background: "white",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      overflow: "hidden",
      height: "auto",
      borderTop: `4px solid ${theme.palette.info.dark}`
    },
    header: {
      padding: "8px 0px 8px 32px",
      background: theme.palette.gardient.medium,
      colors: "white",
      display: "flex",
      flexDirection: "column",
      "& .MuiTypography-root": {
        color: "white"
      },
      "& .MuiTypography-caption": {
        fontSize: "1rem",
        lineHeight: "1.6rem"
      },
      "& .MuiTypography-h6": {
        lineHeight: "2.4rem"
      }
    },
    headerText: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "24px"
    },
    headerTextBold: {
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: "24px"
    },
    searchBox: {
      padding: "24px 24px 16px 24px"
    },
    negativeMargin: {
      margin: "-1.2rem -0.6rem !important"
    },
    gridPadding: {
      padding: ".6rem !important"
    },
    btnContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 0px 12px 0px",
      margin: " 0px 24px 0px 24px "
    },
    successMessage: {
      color: theme.palette.success.main
    },
    info: {
      display: "flex",
      alignItems: "center",
      fontWeight: "bold",
      "& >img": {
        marginRight: 10
      }
    },
    btn: {
      // minWidth: 132,
      maxHeight: 40,
      // marginLeft: 16,
      // padding: "8px 26.5px",
      flex: 1,
      "& .MuiTypography-root": {
        color: "inherit"
      }
    },
    btnTxt: {
      lineHeight: "22px",
      fontSize: "14px"
    },
    gradeWrapper: {
      paddingTop: `24px`
    },
    label: {
      marginBottom: "4px"
    },
    flex: {
      display: "flex",
      padding: ".6rem !important"
    },
    button: {
      display: "flex",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        "& button": {
          margin: "10px 0"
        }
      }
    },
    selectField: {
      height: "36px",
      "& .MuiSelect-root": {
        padding: "0.8rem 1.2rem",
        color: theme.palette.grey[800]
      }
    },
    required: {
      color: theme.palette.error.main,
      "& :after": {
        content: "*"
      }
    },
    messageIcon: { marginRight: "5px" },
    note: {
      fontSize: "14px",
      color: "#000000",
      display: "flex",
      alignItems: "center"
    },
    textError: {
      color: theme.palette.error.light,
      marginTop: 5
    }
  }),
  { name: "MaterialFilter" }
);

interface MaterialFilterProps {
  formik: FormikProps<any>;
  onReset?: () => void;
  onUpload?: () => void;
  onDownload?: () => void;
  resetFilter?: boolean;
  downloadDisable?: boolean;
  setDownloadDisable: Dispatch<SetStateAction<any>>;
}

const MaterialFilter: React.FC<MaterialFilterProps> = ({
  formik,
  onReset,
  onUpload,
  onDownload,
  resetFilter,
  downloadDisable,
  setDownloadDisable
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [reset, setReset] = useState<boolean>(true);
  const [salesOrganizationOptions, setSalesOrganizationOptions] = useState<
    Choice[]
  >([]);
  const [distributionChannelOptions, setDistributionChannelOptions] = useState<
    Choice[]
  >([]);
  const [elementEnableProperty, setElementEnableProperty] = useState<boolean>(
    false
  );
  const [resetButtonEnableProperty, setResetButtonEnableProperty] = useState<
    boolean
  >(false);

  useEffect(() => {
    if (resetFilter) {
      handleResetForm();
    }
  }, [resetFilter]);

  useEffect(() => {
    const isFormNotEmpty =
      formik?.values?.soldTo &&
      formik?.values?.saleOrganization &&
      formik?.values?.distributionChannel;
    setElementEnableProperty(!isFormNotEmpty);
  }, [
    formik?.values?.saleOrganization,
    formik?.values?.soldTo,
    formik?.values?.distributionChannel
  ]);
  useEffect(() => {
    const isResetButtonEnabled =
      !!formik?.values?.soldTo ||
      !!formik?.values?.saleOrganization ||
      !!formik?.values?.distributionChannel;
    setResetButtonEnableProperty(isResetButtonEnabled);
  }, [
    formik?.values?.saleOrganization,
    formik?.values?.soldTo,
    formik?.values?.distributionChannel
  ]);

  useEffect(() => {
    if (!formik?.values?.soldTo) {
      handleResetForm();
    }
  }, [formik?.values?.soldTo]);

  const handleResetForm = () => {
    formik.resetForm();
    setSalesOrganizationOptions([]);
    setDistributionChannelOptions([]);
    setReset(prev => !prev);
    setDownloadDisable(false);
    onReset();
  };

  const handleOnUpload = async () => {
    onUpload();
    setDownloadDisable(true);
  };

  const [
    getDataSoldTo,
    { data: fetchDataSoldTo, loading: loadingDataSoldTo }
  ] = useSearchSuggestionSoldTosCustMatLazyQuery();

  const dataSoldTo = useMemo(() => {
    const data =
      mapEdgesToItems(fetchDataSoldTo?.searchSuggestionSoldTosCustMat) || [];
    return data.map(data => ({
      title: data.domesticSoldToSearchFilterDisplayText,
      value: data.code
    }));
  }, [fetchDataSoldTo?.searchSuggestionSoldTosCustMat]);

  const handleChangeSoldToKeyword = async (value: string) => {
    if (value && value.length >= 2) {
      await getDataSoldTo({
        variables: { first: 100, filter: { search: value } }
      });
    }
  };

  const getValueFromOptionTitle = title =>
    dataSoldTo.find(x => x.title === title)?.value;

  const setDistributionChannelDefaultValue = (options: Choice[]) => {
    const defaultCode: string = "10"; // SEO-8354
    const defaultItem = options.find(option => option.value === defaultCode);
    if (defaultItem) {
      formik.setFieldValue("distributionChannel", defaultItem.value);
    }
  };

  const {
    data: dataSaleOrganization
  } = useGetSaleOrgAndDistChannelBySoldToQuery({
    variables: {
      soldTo: formik?.values?.soldTo || ""
    },
    onCompleted: data => {
      const saleOrganizationChoices =
        data?.getSaleOrgAndDistChannelBySoldTo?.salesOrganization || [];
      const distributionChannels =
        data?.getSaleOrgAndDistChannelBySoldTo?.distributionChannel || [];

      if (saleOrganizationChoices.length > 0) {
        const salesOrganizationOptions: Choice[] = saleOrganizationChoices.map(
          item => ({
            value: item.code,
            label: item.displayText
          })
        );
        setSalesOrganizationOptions(salesOrganizationOptions);
      }
      if (distributionChannels.length > 0) {
        const distributionChannelOptions: Choice[] = distributionChannels.map(
          item => ({
            value: item.code,
            label: item.displayText
          })
        );
        setDistributionChannelOptions(distributionChannelOptions);
        setDistributionChannelDefaultValue(distributionChannelOptions);
      }
    }
  });

  const handleChangeSoldTo = (value: string) => {
    formik.setFieldValue("soldTo", getValueFromOptionTitle(value));
    // Clear related inputs
    formik.setFieldValue("saleOrganization", "");
    formik.setFieldValue("distributionChannel", "");
  };

  const handleChangeSaleOrganization = event => {
    const selectedValue = event.target.value;
    formik.setFieldValue("saleOrganization", selectedValue);

    if (selectedValue) {
      const distribution = dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo?.salesOrgByDistChannel.find(
        org => org.salesOrg === selectedValue
      );
      if (distribution) {
        const formattedData = distribution.distributionChannel.map(channel => ({
          value: channel.code,
          label: channel.displayText
        }));
        setDistributionChannelOptions(formattedData);
      } else {
        setDistributionChannelOptions([]);
      }
    } else {
      const distributionChannels =
        dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo
          ?.distributionChannel || [];
      const formattedData = distributionChannels.map(item => ({
        value: item.code,
        label: item.displayText
      }));
      setDistributionChannelOptions(formattedData);
    }
  };

  const handleChangeDistributionChannels = event => {
    const selectedValue = event.target.value;
    formik.setFieldValue("distributionChannel", selectedValue);

    if (selectedValue) {
      // Assuming distChannelBySalesOrg is an array of objects
      const salesOrgData = dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo?.distChannelBySalesOrg.find(
        org => org.distributionChannel === selectedValue
      );

      if (salesOrgData) {
        // Assuming salesOrgData.salesOrg is an array of objects
        const formattedData = salesOrgData.salesOrg.map(channel => ({
          value: channel.code,
          label: channel.displayText
        }));
        setSalesOrganizationOptions(formattedData);
      } else {
        setSalesOrganizationOptions([]); // Return an empty array if salesOrgData is not found
      }
    } else {
      // Handle case where selectedValue is falsy (e.g., empty string)
      const data = dataSaleOrganization?.getSaleOrgAndDistChannelBySoldTo?.salesOrganization?.map(
        item => ({
          value: item.code,
          label: item.displayText
        })
      );
      setSalesOrganizationOptions(data || []); // Set salesOrganizationOptions to empty array if data is not available
    }
  };

  return (
    <Box className={classes.root}>
      <Grid className={classes.searchBox} container>
        <Grid container spacing={3} className={classes.negativeMargin}>
          <Grid item xs={12} className={classes.gridPadding}>
            <div style={{ display: "flex" }}>
              <MyTypography variant={"subtitle3"}>Sold-to Party</MyTypography>
              <span className={classes.required}>*</span>
              <span>&nbsp;</span>
              <span> (เลือกได้เพียง 1 รายการ)</span>
            </div>
            <Autocomplete
              resetAlways
              optionsProp={dataSoldTo}
              placeholderProps={intl.formatMessage({
                defaultMessage: "ค้นหา Sold-to Code/Name",
                description: "label"
              })}
              onChangeProps={handleChangeSoldToKeyword}
              onSearch={handleChangeSoldTo}
              isReset={reset}
              loading={loadingDataSoldTo}
              id="soldTo"
              name="soldTo"
            />
          </Grid>
          <Grid item xs={6} className={classes.gridPadding}>
            <div style={{ display: "flex" }}>
              <MyTypography variant={"subtitle3"}>
                Sales Organization
              </MyTypography>
              <span className={classes.required}>*</span>
            </div>
            <SingleSelectField
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              testId="saleOrganization"
              name={"saleOrganization"}
              id="saleOrganization"
              value={formik?.values?.saleOrganization}
              choices={salesOrganizationOptions || []}
              onChange={handleChangeSaleOrganization}
              placeholder={"เลือก Sales Organization"}
              className={classes.selectField}
            />
          </Grid>
          <Grid item xs={6} className={classes.gridPadding}>
            <div style={{ display: "flex" }}>
              <MyTypography variant={"subtitle3"}>
                Distribution Channel
              </MyTypography>
              <span className={classes.required}>*</span>
            </div>
            <SingleSelectField
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              testId="distributionChannel"
              name={"distributionChannel"}
              id="distributionChannel"
              value={formik?.values?.distributionChannel}
              onChange={handleChangeDistributionChannels}
              placeholder={"เลือก Distribution Channel"}
              choices={distributionChannelOptions || []}
              className={classes.selectField}
            />
          </Grid>

          <Grid item xs={12} className={classes.gridPadding}>
            <div style={{ display: "flex" }}>
              <p
                style={{
                  color: resetButtonEnableProperty ? "black" : "lightgray",
                  fontWeight: "bold",
                  margin: "0",
                  padding: "0"
                }}
              >
                {"Action"}
              </p>
            </div>
          </Grid>
          <Grid item xs={12} md={4} className={classes.flex}>
            <Button
              variant="contained"
              color="secondary"
              className={classes.btn}
              onClick={onDownload}
              startIcon={<InlineSVG src={downloadIcon} />}
              disabled={elementEnableProperty || downloadDisable}
              name="download"
              id="download"
            >
              <MyTypography
                variant="button2"
                color="inherit"
                className={classes.btnTxt}
              >
                <FormattedMessage defaultMessage="Download Excel" />
              </MyTypography>
            </Button>
          </Grid>
          <Grid item xs={12} md={4} className={classes.flex}>
            <Button
              variant="contained"
              color="secondary"
              className={classes.btn}
              onClick={handleOnUpload}
              disabled={elementEnableProperty}
              startIcon={<InlineSVG src={uploadIcon} />}
              id="upload"
              name="upload"
            >
              <MyTypography
                variant="button2"
                color="inherit"
                className={classes.btnTxt}
              >
                <FormattedMessage defaultMessage="Upload Excel" />
              </MyTypography>
            </Button>
          </Grid>
          <Grid item xs={12} md={4} className={classes.flex}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleResetForm}
              disabled={!resetButtonEnableProperty}
              className={classes.btn}
              id="reset"
              name="reset"
            >
              <InlineSVG src={RefreshIcon} width={24} height={24} />
              <MyTypography variant="body3" className={classes.btnTxt}>
                <FormattedMessage defaultMessage="ล้างข้อมูล" />
              </MyTypography>
            </Button>
          </Grid>
          <Grid item xs={12} md={12} className={classes.gridPadding}>
            <div style={{ display: "flex" }}>
              <Typography className={classes.note}>
                <InlineSVG
                  src={infoIcon}
                  style={{ marginTop: 0 }}
                  className={classes.messageIcon}
                />
                <FormattedMessage defaultMessage="กรุณากรอกข้อมูลที่จำเป็น (" />
                <span className={classes.required}>*</span>
                <FormattedMessage defaultMessage=") และเลือก Action ที่ต้องการ" />
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MaterialFilter;
